import Col from 'antd/lib/col';
import Icon from 'antd/lib/icon';
import Row from 'antd/lib/row';
import Tooltip from 'antd/lib/tooltip';
import classNames from 'classnames';
import moment from 'moment';
import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import { numberFormatted } from 'src/utils/utils';
import { fetchBidDetailsApi } from 'src/api/rfq';
import { QuoteType, RfqBidDetailsType } from '../../SE.interfaces';
import RfqBidDetailsModal from '../../SECommon/hooks/RfqBidsComponents/RfqBidDetailsModal';
import { SEQuoteCardStyles } from './SEQuoteCard.styles';

const styles = (theme) => ({
  ...SEQuoteCardStyles(theme),
});

interface IProps extends StylesProps<ReturnType<typeof styles>> {
  quote: QuoteType;
  deviationFromL1: string;
  /**
   * ISS-292834 — the vendor's own Select-Vendor-Types sub-field selections (e.g. CHA -> Material Category,
   * Plant Type), returned per vendor on the quote-search result. Shown in this card's rank bar. Same for
   * every card in the vendor's tab.
   */
  vendorSelectionFields?: { label: string; value: string }[];
  /**
   * ISS-292834 — the shipment-level rate-match factors (RFQ Type, Mode, Incoterm, POL, POD, Cargo Type).
   * Shown as chips in this card's rank bar alongside the vendor sub-fields. Same for every card in the tab.
   */
  shipmentMatchFactors?: { label: string; value: string }[];
  /** True when this card is the operator's current pick — draws the selected accent. */
  isSelected?: boolean;
  /** Read-only (approval / post-submit) view — disables the hover affordance since the card isn't selectable. */
  disabled?: boolean;
}

const formatAmount = (amount: number): string =>
  numberFormatted(amount, { places: 2, thousandSeparated: true });

/**
 * ISS-292834 — the BE sends the full shipment-level factor set; the FE decides which to surface on the card.
 * Today only the procurement type (Spot/Tender) is shown — the rest (movement type, mode, incoterm, POL/POD,
 * cargo type) are already on the shipment card. Add a label here to reveal more; keep it in sync with the
 * labels the BE emits (getShipmentMatchFactorsForQuote).
 */
const VISIBLE_MATCH_FACTOR_LABELS = ['Procurement Type'];

const SEQuoteCard = (props: IProps): React.ReactElement => {
  const {
    classes, quote, deviationFromL1, vendorSelectionFields, shipmentMatchFactors, isSelected, disabled,
  } = props;
  const ranking = quote?.executionRanking;
  const [isDetailsVisible, setIsDetailsVisible] = React.useState<boolean>(false);
  // ISS-292834 — full charge breakdown fetched lazily from bid-details-v2 on first open (cached after).
  const [detailedRows, setDetailedRows] = React.useState<RfqBidDetailsType[] | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = React.useState<boolean>(false);

  /**
   * ISS-292834 — "View Details" reuses the existing RfqBidDetailsModal (a generic label/value table).
   * Built from the fields the RankedQuote already carries, plus the source RFQ Plan when the backend sends
   * it (`rfqPlanName`, added by the BE follow-up). The modal renders whatever rows are present.
   */
  const buildDetailRows = (): RfqBidDetailsType[] => {
    const rfqPlanName = (quote as { rfqPlanName?: string })?.rfqPlanName;
    const rows: RfqBidDetailsType[] = [
      { key: 'bidId', label: 'Bid ID', value: quote?.bidId || '-' },
      { key: 'vendor', label: 'Vendor', value: quote?.bidderOrganisationName || '-' },
    ];
    if (rfqPlanName) rows.push({ key: 'rfqPlan', label: 'RFQ Plan', value: rfqPlanName });
    rows.push({ key: 'rank', label: 'Rank', value: `${quote?.rank ?? ''}` });
    if (quote?.containerSize) rows.push({ key: 'containerSize', label: 'Container Size', value: quote.containerSize });
    if (quote?.containerType) rows.push({ key: 'containerType', label: 'Container Type', value: quote.containerType });
    if (quote?.validityStartDate) rows.push({ key: 'validityStart', label: 'Validity Start Date', value: moment(quote.validityStartDate).format('DD MMM YYYY') });
    if (quote?.validityEndDate) rows.push({ key: 'validityEnd', label: 'Validity End Date', value: moment(quote.validityEndDate).format('DD MMM YYYY') });
    rows.push({ key: 'total', label: ranking?.name || 'Total', value: `${ranking?.currency ?? ''} ${formatAmount(ranking?.amount)}`.trim() });
    if (quote?.rank !== 1 && deviationFromL1) {
      rows.push({ key: 'deviation', label: 'Deviation from L1', value: `${ranking?.currency ?? ''} ${formatAmount(Number(deviationFromL1))}`.trim() });
    }
    (vendorSelectionFields || []).forEach((f) => rows.push({ key: `vsf-${f.label}`, label: f.label, value: f.value }));
    return rows;
  };

  /**
   * ISS-292834 — the sparse quote object carries no charge breakdown, so "View Details" fetches the full bid
   * from bid-details-v2 (GET /rfq/bid/fetch/detailedV2?bidId) and appends every charge. `bidPriceData` is a
   * map of `{ [chargeCode]: { amount, currency, dollarValue, exchangeRate, prettyName } }` and
   * `bidNonPriceData` a map of `{ [code]: { value, prettyName } }` — the same shape the RFQ CompareBids view
   * renders. On any failure we keep the metadata-only rows (buildDetailRows), so View Details never breaks.
   */
  const buildRowsFromDetailedBid = (detailed: {
    bidPriceData?: Record<string, { amount: number; currency: string; dollarValue: number; exchangeRate: number; prettyName?: string }>;
    bidNonPriceData?: Record<string, { value: string | number; prettyName?: string }>;
  }): RfqBidDetailsType[] => {
    const rows = buildDetailRows();
    const priceData = detailed?.bidPriceData || {};
    Object.keys(priceData).forEach((code) => {
      const charge = priceData[code];
      if (charge && charge.amount !== null && charge.amount !== undefined) {
        rows.push({
          key: `charge-${code}`,
          label: charge.prettyName || code,
          value: {
            amount: charge.amount,
            currency: charge.currency,
            dollarValue: charge.dollarValue,
            exchangeRate: charge.exchangeRate,
          },
        });
      }
    });
    const nonPriceData = detailed?.bidNonPriceData || {};
    Object.keys(nonPriceData).forEach((code) => {
      const item = nonPriceData[code];
      if (item && item.value !== null && item.value !== undefined && `${item.value}` !== '') {
        rows.push({ key: `np-${code}`, label: item.prettyName || code, value: `${item.value}` });
      }
    });
    return rows;
  };

  const openDetails = (): void => {
    setIsDetailsVisible(true);
    // Fetch once per card; reuse the cached breakdown on subsequent opens.
    if (detailedRows || isLoadingDetails || !quote?.bidId) return;
    setIsLoadingDetails(true);
    fetchBidDetailsApi(quote.bidId)
      .then((response) => {
        const detailed = response?.isSuccess ? response.data?.[0] : undefined;
        setDetailedRows(detailed ? buildRowsFromDetailedBid(detailed) : buildDetailRows());
      })
      .catch(() => setDetailedRows(buildDetailRows()))
      .finally(() => setIsLoadingDetails(false));
  };

  const renderIdentity = (): React.ReactElement => (
    <Col span={7} className={classes.cardBodyColumn}>
      <Tooltip title={quote?.bidderOrganisationName || ''}>
        <span className={classes.orgName}>{quote?.bidderOrganisationName || '-'}</span>
      </Tooltip>
      <span className={classes.label}>
        {quote?.isReferenceOnly ? 'Non-Spot L1 (reference)' : 'Vendor'}
      </span>
    </Col>
  );

  const renderValidity = (): React.ReactElement => (
    <>
      <Col span={5} className={classNames(classes.cardBodyColumn, classes.columnBorderLeft)}>
        <span className={classes.value}>
          {quote?.validityStartDate && moment(quote.validityStartDate).format('DD MMM YYYY')}
        </span>
        <span className={classes.label}>Starting Date</span>
      </Col>
      <Col span={5} className={classes.cardBodyColumn}>
        <span className={classes.value}>
          {quote?.validityEndDate && moment(quote.validityEndDate).format('DD MMM YYYY')}
        </span>
        <span className={classes.label}>End Date</span>
      </Col>
    </>
  );

  const renderRates = (): React.ReactElement => (
    <Col span={7} className={classNames(classes.cardBodyColumn, classes.columnBorderLeft)}>
      <span className={classes.boldPrice}>
        {ranking?.currency} {formatAmount(ranking?.amount)}
      </span>
      <span className={classes.label}>{ranking?.name}</span>
      {quote?.rank !== 1 && deviationFromL1 && (
        <span className={classes.deviation}>
          <b>Deviation from L1</b> {ranking?.currency} {formatAmount(Number(deviationFromL1))}
        </span>
      )}
    </Col>
  );

  const renderBottom = (): React.ReactElement => {
    const isTopRank = quote?.rank === 1;
    // ISS-292834 — per-quote attributes shown as chips only when the backend carries them. Container size/type
    // and transit days are populated for Shipping Line and FF only, so they surface just on those cards.
    const quoteAttrChips = [
      quote?.containerSize ? { label: 'Container Size', value: `${quote.containerSize}` } : null,
      quote?.containerType ? { label: 'Container Type', value: `${quote.containerType}` } : null,
      quote?.transitDays ? { label: 'Transit Days', value: `${quote.transitDays}` } : null,
    ].filter(Boolean) as { label: string; value: string }[];
    // BE sends every shipment-level factor; surface only the FE-approved ones (today: Procurement Type).
    const visibleMatchFactors = (shipmentMatchFactors || []).filter((f) => VISIBLE_MATCH_FACTOR_LABELS.includes(f.label));
    // The shipment-level match factors, per-quote attributes, and the vendor's own sub-fields are shown
    // together as chips in the rank bar — no separate header band.
    const chips = [...visibleMatchFactors, ...quoteAttrChips, ...(vendorSelectionFields || [])];
    return (
      <Row className={classes.bottomDiv} type="flex" justify="space-between" align="middle">
        <Col
          className={classNames(
            classes.rankBadge,
            isTopRank ? classes.rankBadgeTop : classes.rankBadgeOther,
          )}
        >
          {isTopRank && <Icon type="trophy" theme="filled" />}
          Rank {quote?.rank}
        </Col>
        {chips.length ? (
          <Col className={classes.subFieldWrap}>
            {chips.map((field) => (
              <span className={classes.subFieldChip} key={field.label}>
                <span className={classes.subFieldLabel}>{field.label}: </span>
                {field.value}
              </span>
            ))}
          </Col>
        ) : null}
        <Col className={classes.bottomActions}>
          {quote?.isReferenceOnly && (
            <span className={classes.referenceNote}>For reference only</span>
          )}
          {/* View Details is a read-only inspect modal — keep it on reference cards too, they're the
              baseline users most want to compare against. */}
          <span
            className={classes.viewDetails}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDetails(); }}
          >
            View Details
            <Icon type="arrow-right" />
          </span>
        </Col>
      </Row>
    );
  };

  return (
    <div
      className={classNames(
        classes.cardDiv,
        !disabled && classes.interactive,
        isSelected && classes.cardSelected,
        quote?.isReferenceOnly && classes.referenceCard,
      )}
    >
      <Row className={classes.cardBody}>
        {renderIdentity()}
        {renderValidity()}
        {renderRates()}
      </Row>
      {renderBottom()}
      {isDetailsVisible && (
        <RfqBidDetailsModal
          visible={isDetailsVisible}
          onClose={() => setIsDetailsVisible(false)}
          bidDetails={detailedRows || buildDetailRows()}
        />
      )}
    </div>
  );
};

export default React.memo(withStyles(styles)(SEQuoteCard));
